import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import Select from "@/components/base/Select";
import { useStore } from "@/context/StoreContext";
import type {
  MenuProduct,
  PaymentMethod,
  PickupMethod,
  TaxInvoice,
} from "@/types";
import { formatSAR } from "@/utils/format";
import PosCart, { type CartLine } from "./components/PosCart";
import PosProductCard from "./components/PosProductCard";

const VAT_RATE = 0.15;

const formatMoment = (date: Date): string => {
  const day = new Intl.DateTimeFormat("ar-EG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat("ar-EG", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${day} — ${time}`;
};

export default function PosPage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { setLastReceipt, products: menuProducts, categories: menuCategories, branches } =
    useStore();
  const openBranches = useMemo(
    () => branches.filter((branch) => branch.isActive && branch.isOpen),
    [branches],
  );

  const branchOptions = openBranches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  }));

  const [branchId, setBranchId] = useState(openBranches[0]?.id ?? "");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [discountInput, setDiscountInput] = useState("");
  const [payment, setPayment] = useState<PaymentMethod>("cash");
  const [pickup, setPickup] = useState<PickupMethod>("branch");
  const [invoiceNo, setInvoiceNo] = useState(1055);
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionSales, setSessionSales] = useState(0);

  // أبقِ الفرع المحدد صالحًا إذا تغيّرت حالة الفروع (إغلاق/تعطيل).
  useEffect(() => {
    if (branchId && !openBranches.some((branch) => branch.id === branchId)) {
      setBranchId(openBranches[0]?.id ?? "");
    }
  }, [openBranches, branchId]);

  const branchName =
    branches.find((branch) => branch.id === branchId)?.name ?? "الفرع الرئيسي";

  const selectedBranch = branches.find((branch) => branch.id === branchId);

  // Same three pickup methods used across branches and orders.
  const pickupMethods = useMemo<PickupMethod[]>(() => {
    const methods: PickupMethod[] = ["branch"];
    if (selectedBranch?.carPickup) methods.push("car");
    if (selectedBranch?.localPickup) methods.push("local");
    return methods;
  }, [selectedBranch]);

  const changeBranch = (value: string) => {
    const target = branches.find((branch) => branch.id === value);
    setBranchId(value);
    setPickup((prev) => {
      if (prev === "car" && !target?.carPickup) return "branch";
      if (prev === "local" && !target?.localPickup) return "branch";
      return prev;
    });
  };

  const visibleProducts = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return menuProducts.filter((product) => {
      const matchCategory =
        category === "all" || product.categoryId === category;
      const matchQuery =
        keyword === "" || product.name.toLowerCase().includes(keyword);
      return matchCategory && matchQuery;
    });
  }, [menuProducts, category, query]);

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (sum, line) => sum + line.product.price * line.quantity,
        0,
      ),
    [cart],
  );

  const discount = useMemo(() => {
    const value = Number(discountInput);
    if (!value || value < 0) return 0;
    return Math.min(value, subtotal);
  }, [discountInput, subtotal]);

  // POS product prices are VAT-inclusive, so the tax is extracted from the
  // price instead of being added on top, and no additional fees are applied.
  const net = Math.round((subtotal - discount) * 100) / 100;
  const vat = Math.round(((net * VAT_RATE) / (1 + VAT_RATE)) * 100) / 100;
  const total = net;

  const quantityOf = (productId: string) =>
    cart.find((line) => line.product.id === productId)?.quantity ?? 0;

  const addProduct = (product: MenuProduct) => {
    if (!product.isAvailable || product.stock <= 0) return;
    setCart((prev) => {
      const existing = prev.find((line) => line.product.id === product.id);
      if (!existing) {
        return [...prev, { product, quantity: 1 }];
      }
      if (existing.quantity >= product.stock) {
        show("الكمية المتاحة من هذا المنتج لا تكفي", "warning");
        return prev;
      }
      return prev.map((line) =>
        line.product.id === product.id
          ? { ...line, quantity: line.quantity + 1 }
          : line,
      );
    });
  };

  const increase = (productId: string) => {
    const line = cart.find((item) => item.product.id === productId);
    if (!line) return;
    if (line.quantity >= line.product.stock) {
      show("الكمية المتاحة من هذا المنتج لا تكفي", "warning");
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item,
      ),
    );
  };

  const decrease = (productId: string) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.product.id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const removeLine = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setDiscountInput("");
    setPayment("cash");
    setPickup("branch");
    show("تم تفريغ الطلب الحالي", "warning");
  };

  const checkout = () => {
    if (cart.length === 0) return;
    const now = new Date();
    const invoice: TaxInvoice = {
      id: `INV-${invoiceNo}`,
      branch: branchName,
      customer: "عميل نقدي",
      phone: "—",
      date: formatMoment(now),
      timestamp: now.toISOString(),
      payment,
      pickup,
      source: "branch",
      invoiceType: "simplified",
      lines: cart.map((line) => ({
        name: line.product.name,
        quantity: line.quantity,
        price: line.product.price,
      })),
      subtotal: Math.round((subtotal - vat) * 100) / 100,
      discount,
      vat,
      total,
    };
    setLastReceipt(invoice);
    setInvoiceNo((prev) => prev + 1);
    setSessionCount((prev) => prev + 1);
    setSessionSales((prev) => Math.round((prev + total) * 100) / 100);
    setCart([]);
    setDiscountInput("");
    setPayment("cash");
    setPickup("branch");
    show(`تم إصدار الفاتورة ${invoice.id} بنجاح`);
    navigate("/pos/receipt");
  };

  return (
    <div className="yc-screen-only space-y-4">
      <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                الفرع
              </label>
              <Select
                value={branchId}
                options={branchOptions}
                onChange={changeBranch}
                ariaLabel="اختيار الفرع"
                className="w-full sm:min-w-[200px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                بحث عن منتج
              </label>
              <div className="flex items-center rounded-btn border border-background-200 bg-white px-3 focus-within:ring-2 focus-within:ring-primary-300">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="اسم المنتج"
                  className="w-full sm:w-56 h-11 bg-transparent px-2 text-sm outline-none placeholder:text-foreground-300"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-box border border-background-200 bg-background-50 px-4 py-3">
              <p className="text-xs text-foreground-500">عمليات الجلسة</p>
              <p className="mt-1 text-xl font-bold text-foreground-950">
                {sessionCount}
              </p>
            </div>
            <div className="rounded-box border border-primary-200 bg-primary-50 px-4 py-3">
              <p className="text-xs text-primary-700">مبيعات الجلسة</p>
              <p className="mt-1 text-xl font-bold text-primary-800">
                {formatSAR(sessionSales)}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
        <div className="space-y-4">
          <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-4">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategory("all")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  category === "all"
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-apps-2-line"></i>
                </span>
                كل الأصناف
              </button>
              {menuCategories.map((item) => {
                const active = category === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCategory(item.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={item.icon}></i>
                    </span>
                    {item.name}
                  </button>
                );
              })}
            </div>
          </section>

          {visibleProducts.length === 0 ? (
            <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card py-20 text-center">
              <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
                <i className="ri-search-eye-line text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-500">
                لا توجد منتجات مطابقة للبحث
              </p>
            </section>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
              {visibleProducts.map((product) => (
                <PosProductCard
                  key={product.id}
                  product={product}
                  quantity={quantityOf(product.id)}
                  onAdd={addProduct}
                />
              ))}
            </div>
          )}
        </div>

        <PosCart
          lines={cart}
          subtotal={subtotal}
          discount={discount}
          vat={vat}
          total={total}
          payment={payment}
          pickup={pickup}
          pickupMethods={pickupMethods}
          onInc={increase}
          onDec={decrease}
          onRemove={removeLine}
          onClear={clearCart}
          onDiscountChange={setDiscountInput}
          onPaymentChange={setPayment}
          onPickupChange={setPickup}
          onCheckout={checkout}
        />
      </div>
    </div>
  );
}